/**
 * The consent ledger (plan E05-T05): find a number's consents, see each one's evidence, revoke one,
 * and import or export the ledger as CSV. Every look at evidence is in the audit log (the API).
 */
import * as Dialog from "@radix-ui/react-dialog";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useEffect, useId, useState, type FormEvent } from "react";
import { data, type Schemas } from "../api/client";
import { useDenied } from "../app/Page";
import { useBff, useCan, useMe } from "../app/session";
import { useFormat, useT } from "../i18n";
import { Button } from "../ui/Button";
import { Empty, ErrorState, Loading, QueryView } from "../ui/states";
import { Table } from "../ui/Table";
import { CSV } from "./files";
import { ImportExport } from "./ImportExport";

type Consent = Schemas["ConsentOut"];
type Status = "all" | "active" | "revoked";
const E164 = /^\+[1-9]\d{6,14}$/;
const field = "rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm";

export function Consents() {
  const t = useT();
  const format = useFormat();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const denied = useDenied();
  const canWrite = useCan("compliance:write");
  const queries = useQueryClient();
  const id = useId();
  const [draft, setDraft] = useState({ e164: "", purpose: "", status: "all" as Status });
  const [filters, setFilters] = useState(draft);
  const [viewing, setViewing] = useState<Consent | null>(null);
  const numberProblem = draft.e164 && !E164.test(draft.e164) ? t("compliance.e164") : null;

  const consents = useInfiniteQuery({
    queryKey: ["consents", tenant, filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      data(api.GET("/v1/consents", { params: { query: {
        limit: 50, cursor: pageParam, e164: filters.e164 || undefined, purpose: filters.purpose || undefined,
        status: filters.status,
      } } })),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  const refresh = () => queries.invalidateQueries({ queryKey: ["consents", tenant] });
  const revoke = useMutation({
    mutationFn: (c: Consent) =>
      data(api.POST("/v1/consents/{consent_id}/revoke", { params: { path: { consent_id: c.id } }, body: {} })),
    onSuccess: refresh,
  });
  const search = (e: FormEvent) => {
    e.preventDefault();
    if (!numberProblem) setFilters(draft);
  };

  return (
    <div className="space-y-4">
      <form onSubmit={search} role="search" aria-label={t("compliance.consents.search")} className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label htmlFor={`${id}-e164`} className="block text-sm font-medium">{t("compliance.number")}</label>
          <input id={`${id}-e164`} className={`${field} font-mono`} value={draft.e164} placeholder="+919876543210"
            aria-invalid={numberProblem ? true : undefined} aria-describedby={numberProblem ? `${id}-e164-error` : undefined}
            onChange={(e) => setDraft({ ...draft, e164: e.target.value.trim() })} />
          {numberProblem && <p id={`${id}-e164-error`} className="text-xs text-[var(--color-danger)]">{numberProblem}</p>}
        </div>
        <div className="space-y-1">
          <label htmlFor={`${id}-purpose`} className="block text-sm font-medium">{t("compliance.consents.purpose")}</label>
          <input id={`${id}-purpose`} className={field} value={draft.purpose} placeholder="promotional"
            onChange={(e) => setDraft({ ...draft, purpose: e.target.value.trim() })} />
        </div>
        <div className="space-y-1">
          <label htmlFor={`${id}-status`} className="block text-sm font-medium">{t("compliance.consents.status")}</label>
          <select id={`${id}-status`} className={field} value={draft.status}
            onChange={(e) => setDraft({ ...draft, status: e.target.value as Status })}>
            {(["all", "active", "revoked"] as const).map((s) => <option key={s} value={s}>{t(`compliance.consents.${s}`)}</option>)}
          </select>
        </div>
        <Button type="submit" variant="secondary">{t("compliance.find")}</Button>
      </form>
      <ImportExport
        name="consents"
        canImport={canWrite}
        send={async (csv) => {
          const r = await data(api.POST("/v1/consents/import", { body: csv, ...CSV }));
          await refresh();
          return { done: t("compliance.consents.imported", { imported: r.imported, skipped: r.skipped }), errors: r.errors };
        }}
        fetchCsv={async () =>
          (await data(api.GET("/v1/consents/export", {
            params: { query: { e164: filters.e164 || undefined, purpose: filters.purpose || undefined, status: filters.status } },
            parseAs: "text",
          }))) as unknown as string}
      />
      {revoke.isError && <ErrorState error={revoke.error} />}
      <QueryView query={consents} denied={denied}>
        {(pages) => {
          const rows = pages.pages.flatMap((p) => p.data);
          if (rows.length === 0) {
            return <Empty title={t("compliance.consents.empty")}><p>{t("compliance.consents.emptyBody")}</p></Empty>;
          }
          return (
            <div className="space-y-3">
              <Table
                caption={t("compliance.tab.consents")}
                rows={rows}
                rowKey={(c) => c.id}
                columns={[
                  { header: t("compliance.number"), cell: (c) => <span className="font-mono">{c.e164}</span> },
                  { header: t("compliance.consents.purpose"), cell: (c) => c.purpose },
                  { header: t("compliance.consents.kind"), cell: (c) => t(`compliance.kind.${c.kind}`) },
                  { header: t("compliance.consents.given"), cell: (c) => format.dateTime(c.granted_at) },
                  {
                    header: t("compliance.consents.status"),
                    cell: (c) => c.revoked_at
                      ? t("compliance.consents.revokedOn", { when: format.dateTime(c.revoked_at) })
                      : c.expires_at
                        ? t("compliance.consents.until", { when: format.dateTime(c.expires_at) })
                        : t("compliance.consents.active"),
                  },
                  {
                    header: t("compliance.consents.actions"),
                    cell: (c) => (
                      <div className="flex gap-2">
                        {c.evidence && (
                          <Button variant="ghost" onClick={() => setViewing(c)}
                            aria-label={t("compliance.evidence.viewFor", { number: c.e164, purpose: c.purpose })}>
                            {t("compliance.evidence.view")}
                          </Button>
                        )}
                        {canWrite && !c.revoked_at && (
                          <Button variant="ghost" disabled={revoke.isPending}
                            aria-label={t("compliance.consents.revokeFor", { number: c.e164, purpose: c.purpose })}
                            onClick={() => {
                              if (window.confirm(t("compliance.consents.revokeConfirm", { number: c.e164, purpose: c.purpose }))) revoke.mutate(c);
                            }}>
                            {t("compliance.consents.revoke")}
                          </Button>
                        )}
                      </div>
                    ),
                  },
                ]}
              />
              {consents.hasNextPage && (
                <Button variant="secondary" disabled={consents.isFetchingNextPage} onClick={() => void consents.fetchNextPage()}>
                  {t("compliance.more")}
                </Button>
              )}
            </div>
          );
        }}
      </QueryView>
      <Evidence consent={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}

/** A consent's proof, shown as what it is: a form's payload, a recording, a scan. */
function Evidence({ consent, onClose }: { consent: Consent | null; onClose: () => void }) {
  const t = useT();
  return (
    <Dialog.Root open={consent !== null} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-12 max-h-[85vh] w-full max-w-2xl -translate-x-1/2 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-6 shadow-xl">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-lg font-semibold">{t("compliance.evidence.title")}</Dialog.Title>
              <Dialog.Description className="text-sm text-[var(--color-muted)]">
                {consent ? t("compliance.evidence.of", { number: consent.e164, purpose: consent.purpose }) : ""}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" aria-label={t("common.close")}><X size={16} aria-hidden="true" /></Button>
            </Dialog.Close>
          </div>
          {consent && <Proof consent={consent} />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Proof({ consent }: { consent: Consent }) {
  const t = useT();
  const { api } = useBff();
  const evidence = consent.evidence;
  const proof = useQuery({
    queryKey: ["consent-evidence", consent.id],
    queryFn: async () => {
      const blob = (await data(api.GET("/v1/consents/{consent_id}/evidence", {
        params: { path: { consent_id: consent.id } }, parseAs: "blob",
      }))) as unknown as Blob;
      return { url: URL.createObjectURL(blob), text: await readable(blob, evidence?.type ?? "") };
    },
    gcTime: 0, // fetched again (and audited) each time it is opened; its object URL goes with it
  });
  const url = proof.data?.url;
  useEffect(() => (url ? () => URL.revokeObjectURL(url) : undefined), [url]);

  if (!evidence) return null;
  if (proof.isPending) return <Loading label={t("app.loading")} />;
  if (proof.isError) return <ErrorState error={proof.error} onRetry={() => void proof.refetch()} />;
  const { type } = evidence;
  const { text } = proof.data;
  return (
    <div className="space-y-3">
      {text !== null && <pre className="max-h-96 overflow-auto rounded-md bg-[var(--color-surface)] p-3 font-mono text-xs">{text}</pre>}
      {url && type.startsWith("image/") && <img src={url} alt={t("compliance.evidence.image")} className="max-h-96" />}
      {url && type.startsWith("audio/") && <audio controls src={url} aria-label={t("compliance.evidence.audio")} className="w-full" />}
      {url && type === "application/pdf" && (
        <object data={url} type="application/pdf" className="h-96 w-full" aria-label={t("compliance.evidence.pdf")}>
          <a href={url} download>{t("compliance.evidence.download")}</a>
        </object>
      )}
      <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-1 text-xs">
        <dt className="text-[var(--color-muted)]">{t("compliance.evidence.type")}</dt><dd className="font-mono">{type}</dd>
        <dt className="text-[var(--color-muted)]">{t("compliance.evidence.size")}</dt><dd>{t("compliance.evidence.bytes", { n: evidence.size_bytes })}</dd>
        <dt className="text-[var(--color-muted)]">SHA-256</dt><dd className="break-all font-mono">{evidence.sha256}</dd>
      </dl>
      {url && (
        <a href={url} download={`${consent.id}-evidence`} className="text-sm underline text-[var(--color-primary)]">
          {t("compliance.evidence.download")}
        </a>
      )}
    </div>
  );
}

/** A JSON or text proof as text to show (JSON indented); null for one shown as itself. */
async function readable(blob: Blob, type: string): Promise<string | null> {
  if (type !== "application/json" && type !== "text/plain") return null;
  const body = await blob.text();
  if (type !== "application/json") return body;
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
}
